import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md font-semibold ring-offset-background transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 active:scale-[0.97] active:transition-transform active:duration-100",
  {
    variants: {
      variant: {
        default:
          "bg-gradient-to-br from-primary to-primary-hover text-primary-foreground shadow-warm-md hover:shadow-warm-lg hover:scale-[1.02]",
        destructive:
          "bg-destructive text-destructive-foreground shadow-warm-sm hover:bg-destructive/90 hover:shadow-warm-md",
        outline:
          "border-[1.5px] border-warm-peach/30 bg-transparent text-primary hover:bg-warm-peach/8 hover:border-primary/30",
        secondary:
          "bg-secondary text-secondary-foreground shadow-warm-sm hover:bg-secondary/80 hover:shadow-warm-md",
        ghost:
          "text-muted-foreground hover:bg-warm-cream/50 hover:text-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-11 px-5 py-2.5 text-sm rounded-md",
        sm: "h-9 px-4 text-sm rounded-md",
        lg: "h-12 px-8 text-base rounded-md",
        icon: "h-10 w-10 rounded-md",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
